import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Camera, Search, Plus, Trash2, Download, X, Check, ArrowRight, RefreshCw, AlertCircle, Settings, Home, LayoutGrid, List as ListIcon, Moon, Sun, Tag, BarChart2, LogOut, Layers, Activity, Menu } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import imageCompression from 'browser-image-compression';
import Login from './Login';
import Register from './Register';
import LocationManager from './LocationManager';
import CategoryManager from './CategoryManager';
import packageJson from '../package.json';

// Modularized Imports
import { API_URL, APP_VERSION } from './api/client';
import client from './api/client';
import { db, generateLocalId, getChoseong } from './db';
import { syncData } from './dbSync';
import { getThemeStyles } from './styles/theme';
import Header from './components/Header';
import BottomNav from './components/BottomNav';
import MenuView from './components/MenuView';
import StatsView from './components/StatsView';
import ItemListView from './components/ItemListView';
import CameraView from './components/CameraView';
import ItemInfoForm from './components/ItemInfoForm';
import FilterPanel from './components/FilterPanel';
import HealthCheckView from './components/HealthCheckView';
import ImageEditor from './components/ImageEditor';

function App() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterLocation, setFilterLocation] = useState('');
  const [sortBy, setSortBy] = useState('newest'); 
  const [showFilters, setShowFilters] = useState(false);
  const [viewMode, setViewMode] = useState(() => {
    const saved = localStorage.getItem('viewMode');
    return saved === 'gallery' ? 'grid' : (saved || 'list');
  }); 
  const [gridColumns, setGridColumns] = useState(localStorage.getItem('gridColumns') || 'auto');
  const [gridDensity, setGridDensity] = useState(localStorage.getItem('gridDensity') || 'comfortable');
  const [gridAspectRatio, setGridAspectRatio] = useState(localStorage.getItem('gridAspectRatio') || '4/3');
  const [viewportWidth, setViewportWidth] = useState(() => (typeof window !== 'undefined' ? window.innerWidth : 1024));
  const [darkMode, setDarkMode] = useState(localStorage.getItem('darkMode') === 'true');
  const [activeTab, setActiveTab] = useState('home'); 
  
  // Auth State
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [authView, setAuthView] = useState('login'); 

  // Metadata
  const [availableCategories, setAvailableCategories] = useState([]);
  const [availableLocations, setAvailableLocations] = useState([]); 
  const [locationTree, setLocationTree] = useState([]); 
  const [availableTags, setAvailableTags] = useState([]);

  const [serverVersion, setServerVersion] = useState(null);
  const [versionMismatch, setVersionMismatch] = useState(false);
  
  // Registration/Edit States
  const [regMode, setRegMode] = useState('list'); // list, camera, info
  const [editId, setEditId] = useState(null); 
  const [newItem, setNewItem] = useState({ name: '', category: '', location: '', location_id: '', quantity: 1, tags: '' });
  const [capturedImage, setCapturedImage] = useState(null);
  const [showImageEditor, setShowImageEditor] = useState(false);
  const [imageEditorSrc, setImageEditorSrc] = useState(null);
  const [imageVersion, setImageVersion] = useState(0); 
  const [statsData, setStatsData] = useState(null);
  const [statsError, setStatsError] = useState(null);
  const [duplicateWarning, setDuplicateWarning] = useState(null);
  const [checkingSimilarity, setCheckingSimilarity] = useState(false);
  const [isInitialSyncing, setIsInitialSyncing] = useState(false);
  const [syncError, setSyncError] = useState(null);
  
  // Camera States
  const [devices, setDevices] = useState([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState(localStorage.getItem('cameraDeviceId') || null);
  const [cameraError, setCameraError] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [resolution, setResolution] = useState(localStorage.getItem('cameraResolution') || 'HD'); 

  const webcamRef = useRef(null);
  const styles = getThemeStyles(darkMode);

  useEffect(() => {
    localStorage.setItem('viewMode', viewMode);
  }, [viewMode]);

  useEffect(() => {
    localStorage.setItem('gridColumns', gridColumns);
  }, [gridColumns]);

  useEffect(() => {
    localStorage.setItem('gridDensity', gridDensity);
  }, [gridDensity]);

  useEffect(() => {
    localStorage.setItem('gridAspectRatio', gridAspectRatio);
  }, [gridAspectRatio]);

  useEffect(() => {
    localStorage.setItem('darkMode', darkMode);
  }, [darkMode]);

  useEffect(() => {
    const handleResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const triggerSync = useCallback(async (force = false) => {
    if (!token) return;
    console.log('Triggering automatic background sync...');
    
    // If local database has 0 items, treat as initial loading phase and show full-screen overlay
    const localCount = await db.items.count();
    const showOverlay = localCount === 0 || force;
    if (showOverlay) {
      setIsInitialSyncing(true);
      setSyncError(null);
    }

    try {
      const res = await syncData();
      if (res.status === 'success') {
        console.log(`Sync completed: Pushed ${res.pushed || 0}, Pulled ${res.pulled || 0}. Refreshing local state.`);
        await fetchItems();
        await fetchMetadata();
        if (showOverlay) {
          setIsInitialSyncing(false);
        }
      } else if (res.status === 'failed') {
        console.error('Background sync failed returned:', res.error);
        if (showOverlay) {
          setSyncError(res.error || '알 수 없는 동기화 오류가 발생했습니다.');
        }
      }
    } catch (err) {
      console.error('Background sync threw error:', err);
      if (showOverlay) {
        setSyncError(err.message || '네트워크 연결이 불안정합니다.');
      }
    }
  }, [token]);

  // Background Sync Engine Trigger
  useEffect(() => {
    if (!token) return;

    // Initial sync on startup
    triggerSync();

    // Sync automatically on reconnect
    window.addEventListener('online', triggerSync);

    // Sync periodically in background every 20 seconds
    const interval = setInterval(triggerSync, 20000);

    return () => {
      window.removeEventListener('online', triggerSync);
      clearInterval(interval);
    };
  }, [token, triggerSync]);

  useEffect(() => {
    if (token) {
      fetchItems();
      fetchMetadata();
      checkVersion();
    }
  }, [token]);

  useEffect(() => {
    if (token) {
        fetchItems();
    }
  }, [search, filterCategory, filterLocation, sortBy]); // Re-fetch when filters change

  const checkVersion = async () => {
    try {
      const response = await client.get('/health');
      setServerVersion(response.data.version);
      if (response.data.version !== APP_VERSION) {
        setVersionMismatch(true);
      }
    } catch (error) {
      console.error('Health check failed:', error);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setAuthView('login');
  };

  const handleDevices = useCallback((mediaDevices) => {
    const videoDevices = mediaDevices.filter(({ kind }) => kind === "videoinput");
    setDevices(videoDevices);
    
    // Check if the currently selected device ID is valid in the newly enumerated devices list
    const isIdValid = selectedDeviceId && videoDevices.some(device => device.deviceId === selectedDeviceId);
    
    if (videoDevices.length > 0 && (!selectedDeviceId || !isIdValid)) {
        // Prefer back camera
        const backCamera = videoDevices.find(device => 
            device.label.toLowerCase().includes('back') || 
            device.label.toLowerCase().includes('environment') ||
            device.label.toLowerCase().includes('후면')
        );
        const newId = backCamera ? backCamera.deviceId : videoDevices[0].deviceId;
        setSelectedDeviceId(newId);
        localStorage.setItem('cameraDeviceId', newId);
    }
  }, [selectedDeviceId]);

  const handleUserMedia = useCallback(() => {
    if (navigator.mediaDevices && navigator.mediaDevices.enumerateDevices) {
      navigator.mediaDevices.enumerateDevices().then(handleDevices).catch(err => {
        console.error("Error re-enumerating devices:", err);
      });
    }
  }, [handleDevices]);

  useEffect(() => {
    if (activeTab === 'add') {
      setRegMode('camera');
      setActiveTab('home');
    }
  }, [activeTab]);

  useEffect(() => {
    if (regMode === 'camera') {
      if (navigator.mediaDevices && navigator.mediaDevices.enumerateDevices) {
        navigator.mediaDevices.enumerateDevices().then(handleDevices).catch(err => {
          console.error("Error enumerating devices:", err);
          setCameraError("카메라 목록을 불러오는데 실패했습니다.");
        });
      } else {
        setCameraError("카메라에 접근할 수 없습니다. (HTTPS 또는 localhost 환경 필요)");
      }
    }
  }, [regMode, handleDevices]);

  const switchCamera = () => {
    if (devices.length < 2) return;
    const currentIndex = devices.findIndex(d => d.deviceId === selectedDeviceId);
    const nextIndex = (currentIndex + 1) % devices.length;
    const newId = devices[nextIndex].deviceId;
    setSelectedDeviceId(newId);
    localStorage.setItem('cameraDeviceId', newId);
  };

  const handleResolutionChange = (newRes) => {
    setResolution(newRes);
    localStorage.setItem('cameraResolution', newRes);
  };

  const handleDeviceSelect = (deviceId) => {
    setSelectedDeviceId(deviceId);
    localStorage.setItem('cameraDeviceId', deviceId);
  };

  const onCameraError = (error) => {
    console.error("Webcam error:", error);
    if (error.name === 'NotAllowedError') {
      setCameraError("카메라 권한이 거부되었습니다. 설정에서 권한을 허용해주세요.");
    } else if (error.name === 'NotFoundError') {
      setCameraError("사용 가능한 카메라를 찾을 수 없습니다.");
    } else {
      setCameraError("카메라를 시작할 수 없습니다. (검은 화면 문제: 브라우저 권한 또는 다른 앱 사용 중 확인)");
    }
  };

  const fetchItems = async () => {
    setLoading(true);
    try {
      let localItems = await db.items.toArray();
      
      // Filter out pending deletes
      localItems = localItems.filter(item => item.sync_status !== 'pending_delete');

      // Apply Search filter
      if (search && search.trim()) {
        const query = search.trim().toLowerCase();
        const isChoseong = /^[ㄱ-ㅎ]+$/.test(query);
        localItems = localItems.filter(item => {
          if (isChoseong) {
            return item.choseong && item.choseong.toLowerCase().includes(query);
          }
          return (
            item.name.toLowerCase().includes(query) ||
            (item.category && item.category.toLowerCase().includes(query)) ||
            (item.location && item.location.toLowerCase().includes(query)) ||
            (item.tags && item.tags.toLowerCase().includes(query))
          );
        });
      }

      // Apply Category filter
      if (filterCategory) {
        localItems = localItems.filter(item => item.category === filterCategory);
      }

      // Apply Location filter
      if (filterLocation) {
        localItems = localItems.filter(item => item.location && item.location.toLowerCase().includes(filterLocation.toLowerCase()));
      }

      // Apply sorting
      if (sortBy === 'newest') {
        localItems.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
      } else if (sortBy === 'oldest') {
        localItems.sort((a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0));
      } else if (sortBy === 'name') {
        localItems.sort((a, b) => a.name.localeCompare(b.name));
      } else if (sortBy === 'location') {
        localItems.sort((a, b) => (a.location || '').localeCompare(b.location || '') || a.name.localeCompare(b.name));
      } else if (sortBy === 'category') {
        localItems.sort((a, b) => (a.category || '').localeCompare(b.category || '') || a.name.localeCompare(b.name));
      }

      setItems(localItems);
    } catch (error) {
      console.error('Error fetching items from Dexie:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMetadata = async () => {
    try {
      const cats = (await db.categories.toArray()).filter(c => c.sync_status !== 'pending_delete');
      const locs = (await db.locations.toArray()).filter(l => l.sync_status !== 'pending_delete');
      const tags = (await db.tags.toArray()).filter(t => t.sync_status !== 'pending_delete');

      // Map for FilterPanel
      const mappedCategories = cats.map(cat => ({
        ...cat,
        value: cat.name,
        label: cat.name
      }));
      
      const mappedLocations = locs.map(loc => ({
        ...loc,
        value: loc.name,
        label: loc.name
      }));

      // Rebuild Location Tree
      const locMap = {};
      locs.forEach(loc => {
        locMap[loc.id] = {
          id: loc.id,
          name: loc.name,
          parent_id: loc.parent_id,
          children: []
        };
      });

      const roots = [];
      locs.forEach(loc => {
        if (loc.parent_id && locMap[loc.parent_id]) {
          locMap[loc.parent_id].children.push(locMap[loc.id]);
        } else {
          roots.push(locMap[loc.id]);
        }
      });

      setAvailableCategories(mappedCategories);
      setAvailableLocations(mappedLocations);
      setLocationTree(roots);
      setAvailableTags(tags);
    } catch (error) {
      console.error('Error fetching metadata from Dexie:', error);
    }
  };

  const fetchStats = async () => {
    setStatsError(null);
    try {
      const activeItems = (await db.items.toArray()).filter(i => i.sync_status !== 'pending_delete');
      
      const totalItems = activeItems.length;
      const totalQuantity = activeItems.reduce((acc, item) => acc + (item.quantity || 1), 0);
      
      // Calculate Category distribution
      const categoryCounts = {};
      activeItems.forEach(item => {
        const cat = item.category || '미지정';
        categoryCounts[cat] = (categoryCounts[cat] || 0) + (item.quantity || 1);
      });
      const topCategory = Object.entries(categoryCounts).sort((a, b) => b[1] - a[1])[0]?.[0] || '없음';

      // Calculate Location distribution
      const locationCounts = {};
      activeItems.forEach(item => {
        const loc = item.location || '미지정';
        locationCounts[loc] = (locationCounts[loc] || 0) + (item.quantity || 1);
      });
      const topLocation = Object.entries(locationCounts).sort((a, b) => b[1] - a[1])[0]?.[0] || '없음';

      // Calculate category_stats array for PieChart
      const category_stats = Object.entries(categoryCounts).map(([name, value]) => ({
        name,
        value
      }));

      // Calculate location_stats array for BarChart
      const location_stats = Object.entries(locationCounts).map(([name, value]) => ({
        name,
        value
      }));

      // Calculate monthly_stats array for LineChart (Group by YYYY-MM)
      const monthlyCounts = {};
      activeItems.forEach(item => {
        const dateStr = item.created_at || item.updated_at || new Date().toISOString();
        const month = dateStr.substring(0, 7); // "YYYY-MM"
        monthlyCounts[month] = (monthlyCounts[month] || 0) + 1;
      });
      const monthly_stats = Object.entries(monthlyCounts)
        .sort((a, b) => a[0].localeCompare(b[0]))
        .map(([name, value]) => ({
          name,
          value
        }));

      const stats = {
        total_items: totalItems,
        total_quantity: totalQuantity,
        recent_activity: activeItems
          .sort((a, b) => new Date(b.updated_at || 0) - new Date(a.updated_at || 0))
          .slice(0, 5)
          .map(item => ({
            id: item.id,
            name: item.name,
            category: item.category,
            location: item.location,
            quantity: item.quantity || 1,
            updated_at: item.updated_at
          })),
        category_stats,
        location_stats,
        monthly_stats,
        analysis: {
          top_category: topCategory,
          top_location: topLocation
        }
      };

      setStatsData(stats);
    } catch (error) {
      console.error('Error fetching stats offline:', error);
      setStatsError('통계 데이터를 불러오는데 실패했습니다.');
    }
  };

  useEffect(() => {
    if (activeTab === 'stats') {
      fetchStats();
    }
  }, [activeTab]);

  const capture = useCallback(() => {
    const imageSrc = webcamRef.current.getScreenshot();
    setCapturedImage(imageSrc);
    setRegMode('info');
  }, [webcamRef]);

  const handleImageUpload = async (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      
      const options = {
        maxSizeMB: 1,
        maxWidthOrHeight: 1920,
        useWebWorker: true
      };

      try {
        const compressedFile = await imageCompression(file, options);
        
        const reader = new FileReader();
        reader.onload = (ev) => {
          setCapturedImage(ev.target.result);
          setRegMode('info');
        };
        reader.readAsDataURL(compressedFile);
      } catch (error) {
        console.error('Image compression failed:', error);
        // Fallback to original file
        const reader = new FileReader();
        reader.onload = (ev) => {
          setCapturedImage(ev.target.result);
          setRegMode('info');
        };
        reader.readAsDataURL(file);
      }
    }
  };

  const handleRotate = async (e) => {
    e.preventDefault(); 
    if (!editId) return;
    try {
      await client.post(`/items/${editId}/rotate`);
      setImageVersion(prev => prev + 1);
      fetchItems(); 
    } catch (error) {
      console.error('Error rotating image:', error);
      alert('이미지 회전 중 오류가 발생했습니다.');
    }
  };

  const handleOpenImageEditor = async () => {
    if (capturedImage) {
      setImageEditorSrc(capturedImage);
      setShowImageEditor(true);
    } else if (editId) {
      const item = items.find(i => i.id === editId);
      if (item) {
        const imageUrl = getFullImageUrl(item);
        if (imageUrl) {
          try {
            const response = await client.get(imageUrl, { responseType: 'blob' });
            const localUrl = URL.createObjectURL(response.data);
            setImageEditorSrc(localUrl);
            setShowImageEditor(true);
          } catch (err) {
            console.error('Failed to fetch image for editing:', err);
            alert('편집용 이미지를 로드하는 중 오류가 발생했습니다. 네트워크 연결 상태를 확인해 주세요.');
          }
        }
      }
    }
  };

  const handleSaveEditedImage = (editedDataUrl) => {
    setCapturedImage(editedDataUrl);
    setShowImageEditor(false);
  };

  const checkNameDuplicate = async (name) => {
    if (!name || !name.trim()) {
        setDuplicateWarning(prev => ({ ...prev, nameExact: [], nameSimilar: [] }));
        return;
    }
    try {
        let url = `/items/check-duplicate-name?name=${encodeURIComponent(name)}`;
        if (editId) {
            url += `&exclude_id=${editId}`;
        }
        const response = await client.get(url);
        const { exact, similar } = response.data;
        setDuplicateWarning(prev => ({ 
            ...prev, 
            nameExact: exact, 
            nameSimilar: similar 
        }));
    } catch (error) {
        console.error("Error checking name duplicate", error);
    }
  };

  const checkImageDuplicate = async (blob) => {
    if (!blob) {
        setDuplicateWarning(prev => ({ ...prev, imageSimilar: [] }));
        return;
    }
    const formData = new FormData();
    formData.append('file', blob);
    if (editId) {
        formData.append('exclude_id', editId);
    }
    setCheckingSimilarity(true);
    try {
        const response = await client.post(`/items/check-duplicate-image`, formData);
        const { similar } = response.data;
        setDuplicateWarning(prev => ({
            ...prev,
            imageSimilar: similar
        }));
    } catch (error) {
        console.error("Error checking image duplicate", error);
    } finally {
        setCheckingSimilarity(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
        if (regMode === 'info') {
            if (newItem.name) {
                checkNameDuplicate(newItem.name);
            } else {
                setDuplicateWarning(prev => ({ ...prev, nameExact: [], nameSimilar: [] }));
            }

            if (capturedImage) {
                 fetch(capturedImage)
                 .then(res => res.blob())
                 .then(blob => checkImageDuplicate(blob));
            } else {
                setDuplicateWarning(prev => ({ ...prev, imageSimilar: [] }));
            }
        } else {
            setDuplicateWarning({ nameExact: [], nameSimilar: [], imageSimilar: [] });
        }
    }, 800); 
    return () => clearTimeout(timer);
  }, [newItem.name, capturedImage, regMode]);

  const handleAddNext = async (e) => {
    e.preventDefault();
    if (editId) return;
    
    if (!newItem.name.trim()) { alert('품명을 입력해주세요.'); return; }
    if (!newItem.location.trim()) { alert('위치를 입력해주세요.'); return; }

    try {
      // 1. Resolve Location offline
      let finalLocId = newItem.location_id;
      if (!finalLocId && newItem.location) {
        const allLocs = await db.locations.toArray();
        const existing = allLocs.find(loc => 
          loc.name.toLowerCase() === newItem.location.trim().toLowerCase() && 
          loc.sync_status !== 'pending_delete'
        );
        if (existing) {
          finalLocId = existing.id;
        } else {
          const newLocId = generateLocalId('local_loc_');
          await db.locations.put({
            id: newLocId,
            name: newItem.location.trim(),
            parent_id: null,
            sync_status: 'pending_upload',
            updated_at: new Date().toISOString()
          });
          finalLocId = newLocId;
        }
      }

      // 2. Resolve Category offline
      let finalCatId = newItem.category_id;
      if (!finalCatId && newItem.category) {
        const allCats = await db.categories.toArray();
        const existing = allCats.find(cat => 
          cat.name.toLowerCase() === newItem.category.trim().toLowerCase() && 
          cat.sync_status !== 'pending_delete'
        );
        if (existing) {
          finalCatId = existing.id;
        } else {
          const newCatId = generateLocalId('local_cat_');
          await db.categories.put({
            id: newCatId,
            name: newItem.category.trim(),
            sync_status: 'pending_upload',
            updated_at: new Date().toISOString()
          });
          finalCatId = newCatId;
        }
      }

      // 3. Resolve Tags offline
      if (newItem.tags) {
        const tagNames = newItem.tags.split(',').map(t => t.trim()).filter(Boolean);
        const allTags = await db.tags.toArray();
        for (const tName of tagNames) {
          const existing = allTags.find(t => 
            t.name.toLowerCase() === tName.toLowerCase() && 
            t.sync_status !== 'pending_delete'
          );
          if (!existing) {
            await db.tags.put({
              id: generateLocalId('local_tag_'),
              name: tName,
              sync_status: 'pending_upload',
              updated_at: new Date().toISOString()
            });
          }
        }
      }

      // 4. Convert base64 Image to Blob
      let localImageBlob = null;
      if (capturedImage) {
        localImageBlob = await fetch(capturedImage).then(res => res.blob());
      }

      const tempItemId = generateLocalId('local_item_');
      const combinedText = `${newItem.name.trim()} ${newItem.category || ''} ${newItem.location} ${newItem.tags || ''}`;
      
      const itemRecord = {
        id: tempItemId,
        name: newItem.name.trim(),
        category: newItem.category ? newItem.category.trim() : null,
        category_id: finalCatId,
        location: newItem.location.trim(),
        location_id: finalLocId,
        quantity: newItem.quantity || 1,
        tags: newItem.tags || null,
        image_path: null,
        thumbnail_path: null,
        choseong: getChoseong(combinedText),
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        sync_status: 'pending_upload',
        local_image_blob: localImageBlob
      };

      await db.items.put(itemRecord);

      setNewItem(prev => ({ ...prev, name: '', quantity: 1, tags: '' }));
      setCapturedImage(null);
      setEditId(null);
      setRegMode('camera'); 
      fetchItems();
      fetchMetadata();
    } catch (error) {
      console.error('Error adding item locally:', error);
      alert('등록 중 오류가 발생했습니다.');
    }
  };

  const handleAddItem = async (e) => {
    e.preventDefault();
    if (!newItem.name.trim()) { alert('품명을 입력해주세요.'); return; }
    if (!newItem.location.trim()) { alert('위치를 입력해주세요.'); return; }

    try {
      // 1. Resolve Location offline
      let finalLocId = newItem.location_id;
      if (!finalLocId && newItem.location) {
        const allLocs = await db.locations.toArray();
        const existing = allLocs.find(loc => 
          loc.name.toLowerCase() === newItem.location.trim().toLowerCase() && 
          loc.sync_status !== 'pending_delete'
        );
        if (existing) {
          finalLocId = existing.id;
        } else {
          const newLocId = generateLocalId('local_loc_');
          await db.locations.put({
            id: newLocId,
            name: newItem.location.trim(),
            parent_id: null,
            sync_status: 'pending_upload',
            updated_at: new Date().toISOString()
          });
          finalLocId = newLocId;
        }
      }

      // 2. Resolve Category offline
      let finalCatId = newItem.category_id;
      if (!finalCatId && newItem.category) {
        const allCats = await db.categories.toArray();
        const existing = allCats.find(cat => 
          cat.name.toLowerCase() === newItem.category.trim().toLowerCase() && 
          cat.sync_status !== 'pending_delete'
        );
        if (existing) {
          finalCatId = existing.id;
        } else {
          const newCatId = generateLocalId('local_cat_');
          await db.categories.put({
            id: newCatId,
            name: newItem.category.trim(),
            sync_status: 'pending_upload',
            updated_at: new Date().toISOString()
          });
          finalCatId = newCatId;
        }
      }

      // 3. Resolve Tags offline
      if (newItem.tags) {
        const tagNames = newItem.tags.split(',').map(t => t.trim()).filter(Boolean);
        const allTags = await db.tags.toArray();
        for (const tName of tagNames) {
          const existing = allTags.find(t => 
            t.name.toLowerCase() === tName.toLowerCase() && 
            t.sync_status !== 'pending_delete'
          );
          if (!existing) {
            await db.tags.put({
              id: generateLocalId('local_tag_'),
              name: tName,
              sync_status: 'pending_upload',
              updated_at: new Date().toISOString()
            });
          }
        }
      }

      if (editId) {
        const combinedText = `${newItem.name.trim()} ${newItem.category || ''} ${newItem.location} ${newItem.tags || ''}`;
        
        let localImageBlob = undefined;
        if (capturedImage) {
          try {
            localImageBlob = await fetch(capturedImage).then(res => res.blob());
          } catch (fetchErr) {
            console.error('Error fetching captured image blob:', fetchErr);
          }
        }

        const updateData = {
          name: newItem.name.trim(),
          category: newItem.category ? newItem.category.trim() : null,
          category_id: finalCatId,
          location: newItem.location.trim(),
          location_id: finalLocId,
          quantity: newItem.quantity || 1,
          tags: newItem.tags || null,
          choseong: getChoseong(combinedText),
          sync_status: 'pending_upload',
          updated_at: new Date().toISOString()
        };

        if (localImageBlob !== undefined) {
          updateData.local_image_blob = localImageBlob;
          // Set image_path and thumbnail_path to null to show we are uploading a new image
          updateData.image_path = null;
          updateData.thumbnail_path = null;
        }

        await db.items.update(editId, updateData);
        resetRegistration();
        fetchItems();
        fetchMetadata();
      } else {
        let localImageBlob = null;
        if (capturedImage) {
          localImageBlob = await fetch(capturedImage).then(res => res.blob());
        }

        const tempItemId = generateLocalId('local_item_');
        const combinedText = `${newItem.name.trim()} ${newItem.category || ''} ${newItem.location} ${newItem.tags || ''}`;
        
        const itemRecord = {
          id: tempItemId,
          name: newItem.name.trim(),
          category: newItem.category ? newItem.category.trim() : null,
          category_id: finalCatId,
          location: newItem.location.trim(),
          location_id: finalLocId,
          quantity: newItem.quantity || 1,
          tags: newItem.tags || null,
          image_path: null,
          thumbnail_path: null,
          choseong: getChoseong(combinedText),
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          sync_status: 'pending_upload',
          local_image_blob: localImageBlob
        };

        await db.items.put(itemRecord);
        resetRegistration();
        fetchItems();
        fetchMetadata();
      }
    } catch (error) {
      console.error('Error saving item locally:', error);
      alert('저장 중 오류가 발생했습니다.');
    }
  };

  const resetRegistration = () => {
    setNewItem({ name: '', category: '', location: '', location_id: '', quantity: 1, tags: '' });
    setCapturedImage(null);
    setEditId(null);
    setRegMode('list');
    setActiveTab('home');
  };

  const handleEdit = (item) => {
    setNewItem({
      name: item.name,
      category: item.category || '',
      location: item.location,
      location_id: item.location_id || '',
      quantity: item.quantity,
      tags: item.tags || ''
    });
    setCapturedImage(null);
    setEditId(item.id);
    setRegMode('info'); 
  };

  const handleMergeQuantity = async (existingItem) => {
    if (!window.confirm(`'${existingItem.name}'의 수량을 ${newItem.quantity}개 추가하시겠습니까?\n(현재: ${existingItem.quantity}개 -> 변경: ${existingItem.quantity + newItem.quantity}개)`)) return;

    try {
      await db.items.update(existingItem.id, {
        quantity: existingItem.quantity + newItem.quantity,
        sync_status: 'pending_upload',
        updated_at: new Date().toISOString()
      });
      alert('수량이 합쳐졌습니다.');
      resetRegistration();
      fetchItems();
      fetchStats();
    } catch (error) {
      console.error('Error merging item locally:', error);
      alert('병합 중 오류가 발생했습니다.');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('정말 삭제하시겠습니까?')) {
      try {
        if (String(id).startsWith('local_')) {
          await db.items.delete(id);
        } else {
          await db.items.update(id, {
            sync_status: 'pending_delete',
            updated_at: new Date().toISOString()
          });
        }
        fetchItems();
        fetchStats();
      } catch (error) {
        console.error('Error deleting item locally:', error);
      }
    }
  };

  const handleExport = () => {
    window.open(`${API_URL}/export/excel`, '_blank');
  };

  const getFullImageUrl = (item) => {
    if (item.local_image_blob) {
      return URL.createObjectURL(item.local_image_blob);
    }
    if (!item.image_path) return null;
    const filename = item.image_path.split(/[/\\]/).pop();
    return `${API_URL}/uploads/${filename}`;
  };

  const resetFilters = () => {
    setFilterCategory('');
    setFilterLocation('');
    setSortBy('newest');
    setSearch('');
  };

  if (!token) {
    if (authView === 'register') {
      return <Register setAuthView={setAuthView} darkMode={darkMode} />;
    }
    return <Login setToken={setToken} setAuthView={setAuthView} darkMode={darkMode} />;
  }

  return (
    <div style={styles.container}>
      {/* Header and Filter (Only in list mode) */}
      <AnimatePresence>
        {regMode === 'list' && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            <Header 
              styles={styles}
              search={search}
              setSearch={setSearch}
              showFilters={showFilters}
              setShowFilters={setShowFilters}
              handleExport={handleExport}
              checkVersion={checkVersion}
              serverVersion={serverVersion}
              versionMismatch={versionMismatch}
              viewMode={viewMode}
              setViewMode={setViewMode}
            />
            <FilterPanel 
              showFilters={showFilters}
              filterCategory={filterCategory}
              setFilterCategory={setFilterCategory}
              filterLocation={filterLocation}
              setFilterLocation={setFilterLocation}
              sortBy={sortBy}
              setSortBy={setSortBy}
              availableCategories={availableCategories}
              availableLocations={availableLocations}
              resetFilters={resetFilters}
              styles={styles}
              darkMode={darkMode}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main Content */}
      <main style={{flex: 1, overflowY: 'auto', paddingBottom: '80px', position: 'relative'}}>
        <AnimatePresence mode="wait">
          {regMode === 'list' ? (
             <motion.div
               key="list-mode"
               initial={{ opacity: 0 }}
               animate={{ opacity: 1 }}
               exit={{ opacity: 0 }}
               transition={{ duration: 0.3 }}
               style={{ height: '100%' }}
             >
               {activeTab === 'home' || activeTab === 'search' ? (
                 <ItemListView 
                   key={`item-list-${viewMode}`}
                   items={items}
                   loading={loading}
                   viewMode={viewMode}
                   gridColumns={gridColumns}
                   gridDensity={gridDensity}
                   gridAspectRatio={gridAspectRatio}
                   viewportWidth={viewportWidth}
                   handleEdit={handleEdit}
                   handleDelete={handleDelete}
                   fetchItems={fetchItems}
                   styles={styles}
                   darkMode={darkMode}
                 />
               ) : activeTab === 'locations' ? (
                 <LocationManager 
                   tree={locationTree} 
                   refreshTree={fetchMetadata} 
                   darkMode={darkMode} 
                 />
               ) : activeTab === 'categories' ? (
                 <CategoryManager 
                   refreshCategories={fetchMetadata} 
                   darkMode={darkMode} 
                 />
               ) : activeTab === 'stats' ? (
                 <StatsView 
                   statsData={statsData} 
                   statsError={statsError} 
                   fetchStats={fetchStats} 
                   darkMode={darkMode} 
                   styles={styles} 
                 />
               ) : activeTab === 'menu' ? (
                 <MenuView 
                   activeTab={activeTab} 
                   setActiveTab={setActiveTab} 
                   darkMode={darkMode} 
                   setDarkMode={setDarkMode} 
                   handleLogout={handleLogout} 
                   styles={styles} 
                 />
               ) : activeTab === 'health' ? (
                 <HealthCheckView darkMode={darkMode} styles={styles} />
               ) : null}
             </motion.div>
          ) : regMode === 'camera' ? (
            <motion.div
              key="camera-mode"
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: 20 }}
            >
              <CameraView 
                webcamRef={webcamRef}
                devices={devices}
                selectedDeviceId={selectedDeviceId}
                cameraError={cameraError}
                showSettings={showSettings}
                setShowSettings={setShowSettings}
                resolution={resolution}
                switchCamera={switchCamera}
                handleResolutionChange={handleResolutionChange}
                handleDeviceSelect={handleDeviceSelect}
                capture={capture}
                onCameraError={onCameraError}
                setRegMode={setRegMode}
                styles={styles}
                onImageUpload={handleImageUpload}
                onCameraReady={handleUserMedia}
                editId={editId}
              />
            </motion.div>
          ) : regMode === 'info' ? (
            <motion.div
              key="info-mode"
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: 20 }}
            >
              <ItemInfoForm 
                newItem={newItem}
                setNewItem={setNewItem}
                availableCategories={availableCategories}
                availableLocations={availableLocations}
                availableTags={availableTags}
                duplicateWarning={duplicateWarning}
                handleEdit={handleEdit}
                handleMergeQuantity={handleMergeQuantity}
                handleAddNext={handleAddNext}
                handleAddItem={handleAddItem}
                capturedImage={capturedImage}
                editId={editId}
                imageVersion={imageVersion}
                handleRotate={handleRotate}
                items={items}
                getFullImageUrl={getFullImageUrl}
                setRegMode={setRegMode}
                setEditId={setEditId}
                styles={styles}
                darkMode={darkMode}
                handleOpenImageEditor={handleOpenImageEditor}
              />
            </motion.div>
          ) : null}
        </AnimatePresence>
      </main>

      {/* Bottom Navigation */}
      {regMode === 'list' && (
        <BottomNav 
          activeTab={activeTab} 
          setActiveTab={setActiveTab} 
          setRegMode={setRegMode} 
          styles={styles} 
        />
      )}
      <AnimatePresence>
        {isInitialSyncing && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: darkMode ? 'rgba(18, 18, 18, 0.95)' : 'rgba(255, 255, 255, 0.95)',
              zIndex: 9999,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '20px',
              textAlign: 'center'
            }}
          >
            {!syncError ? (
              <>
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ repeat: Infinity, duration: 1.5, ease: "linear" }}
                  style={{
                    marginBottom: '20px',
                    color: '#0088FE'
                  }}
                >
                  <RefreshCw size={48} />
                </motion.div>
                <h3 style={{ 
                  fontSize: '18px', 
                  fontWeight: 'bold', 
                  marginBottom: '10px',
                  color: darkMode ? '#eee' : '#333'
                }}>
                  서버와 데이터 동기화 중
                </h3>
                <p style={{ 
                  fontSize: '14px', 
                  color: darkMode ? '#aaa' : '#666',
                  maxWidth: '300px',
                  lineHeight: '1.5'
                }}>
                  초기 데이터베이스를 클라우드 서버와 이식하고 있습니다. 잠시만 기다려 주세요...
                </p>
              </>
            ) : (
              <>
                <AlertCircle size={48} color="#ff4d4d" style={{ marginBottom: '20px' }} />
                <h3 style={{ 
                  fontSize: '18px', 
                  fontWeight: 'bold', 
                  marginBottom: '10px',
                  color: '#ff4d4d'
                }}>
                  동기화 실패
                </h3>
                <p style={{ 
                  fontSize: '14px', 
                  color: darkMode ? '#eee' : '#444',
                  maxWidth: '300px',
                  lineHeight: '1.5',
                  marginBottom: '25px'
                }}>
                  {syncError}
                </p>
                <div style={{ display: 'flex', gap: '15px' }}>
                  <button 
                    onClick={() => triggerSync(true)} 
                    style={{
                      padding: '12px 24px',
                      borderRadius: '10px',
                      backgroundColor: '#0088FE',
                      color: 'white',
                      border: 'none',
                      fontWeight: 'bold',
                      fontSize: '14px',
                      boxShadow: '0 4px 6px rgba(0, 136, 254, 0.2)',
                      cursor: 'pointer'
                    }}
                  >
                    다시 시도
                  </button>
                  <button 
                    onClick={() => setIsInitialSyncing(false)} 
                    style={{
                      padding: '12px 24px',
                      borderRadius: '10px',
                      backgroundColor: darkMode ? '#444' : '#e0e0e0',
                      color: darkMode ? '#fff' : '#333',
                      border: 'none',
                      fontWeight: 'bold',
                      fontSize: '14px',
                      cursor: 'pointer'
                    }}
                  >
                    닫기
                  </button>
                </div>
              </>
            )}
          </motion.div>
        )}
        {showImageEditor && (
          <ImageEditor 
            imageSrc={imageEditorSrc}
            onSave={handleSaveEditedImage}
            onCancel={() => setShowImageEditor(false)}
            darkMode={darkMode}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

export default App;
